import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';

import { Services } from 'services';
import { useAppSelector } from 'modules/state-mgmt/store';
import { prettifyErrorResponse } from 'utils';
import {
  DrugTestingScheduleResult,
  IDrugTestingType,
  IProjectDrugTestingPanel,
  IWorkerProjectDetail,
} from 'models/drugTesting';

import { ISelectOption } from './useBookAppointmentOptions';

export type { ISelectOption };

// Panel option carries the BAT flag so the Result options can react to the selection.
export interface IPanelOption extends ISelectOption {
  isBatPanel: boolean;
}

export const useUploadResultsOptions = (
  selectedProjectId?: string,
  selectedPanelId?: string,
  enabled: boolean = true,
) => {
  const { t } = useTranslation();
  const { workerId, languageCode, isWorker } = useAppSelector(state => ({
    workerId: state.onboarding.id || state.user?.workerId,
    languageCode: state.app?.languageCode,
    isWorker: state.user?.isWorker,
  }));

  const [projects, setProjects] = useState<ISelectOption[]>([]);
  // projectId → workerProjectId, needed to fetch the project detail (panels live there).
  const [workerProjectIdMap, setWorkerProjectIdMap] = useState<Record<string, string>>({});
  const [panels, setPanels] = useState<IPanelOption[]>([]);
  const [types, setTypes] = useState<ISelectOption[]>([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [isLoadingPanels, setIsLoadingPanels] = useState(false);
  const [isLoadingTypes, setIsLoadingTypes] = useState(false);

  // Only projects with drug testing active AND third-party uploads allowed are eligible.
  useEffect(() => {
    if (!enabled || !workerId) return;

    setIsLoadingProjects(true);
    Services.getWorkerLiteProjects(workerId, 1, undefined, 1, undefined, languageCode || 'en', true)
      .then(response => {
        const items = response.data?.items || [];
        const validItems = items.filter(
          (item: any) => item.project?.id && item.isDrugTestingActive && item.allowThirdPartyDrugTestResults,
        );
        setProjects(validItems.map((item: any) => ({ value: item.project.id, label: item.project.name || '' })));
        const map: Record<string, string> = {};
        validItems.forEach((item: any) => {
          // workerProjectId now lives on the first company entry (the top-level field is a zero GUID).
          map[item.project.id] = item.companies?.[0]?.workerProjectId || '';
        });
        setWorkerProjectIdMap(map);
      })
      .catch(error => {
        enqueueSnackbar(prettifyErrorResponse(error), { variant: 'error' });
      })
      .finally(() => {
        setIsLoadingProjects(false);
      });
  }, [enabled, workerId, languageCode]);

  // Panels come from the selected project's detail (fetched via its workerProjectId).
  useEffect(() => {
    const workerProjectId = selectedProjectId ? workerProjectIdMap[selectedProjectId] : '';
    if (!selectedProjectId || !workerProjectId) {
      setPanels([]);
      return;
    }

    setIsLoadingPanels(true);
    Services.getWorkerProjectDetail(workerId, workerProjectId, languageCode || 'en', isWorker || false)
      .then(response => {
        const detail: IWorkerProjectDetail = response.data || {};
        const panelList: IProjectDrugTestingPanel[] = detail.drugTestingPanels || [];
        setPanels(
          panelList.map(panel => ({
            value: panel.entityId,
            label: panel.entityName || '',
            isBatPanel: !!panel.isBatPanel,
          })),
        );
      })
      .catch(error => {
        enqueueSnackbar(prettifyErrorResponse(error), { variant: 'error' });
        setPanels([]);
      })
      .finally(() => {
        setIsLoadingPanels(false);
      });
  }, [selectedProjectId, workerProjectIdMap, workerId, languageCode, isWorker]);

  // Types still come from the dedicated project-types endpoint.
  useEffect(() => {
    if (!selectedProjectId) {
      setTypes([]);
      return;
    }

    setIsLoadingTypes(true);
    Services.getProjectDrugTestingTypes(selectedProjectId, languageCode || 'en')
      .then(response => {
        const typeList: IDrugTestingType[] = response.data || [];
        // Only Standard and Renewal are valid for third-party uploads.
        const allowedTypes = ['standard', 'renewal'];
        setTypes(
          typeList
            .filter(type => allowedTypes.includes((type.name || '').trim().toLowerCase()))
            .map(type => ({ value: type.id, label: type.name || '' })),
        );
      })
      .catch(error => {
        enqueueSnackbar(prettifyErrorResponse(error), { variant: 'error' });
        setTypes([]);
      })
      .finally(() => {
        setIsLoadingTypes(false);
      });
  }, [selectedProjectId, languageCode]);

  // Result options depend on the selected panel's BAT flag:
  // BAT panel → only "BAT Pass"; non-BAT panel → only "Negative"; no panel → none.
  const results = useMemo<ISelectOption[]>(() => {
    const panel = panels.find(p => p.value === selectedPanelId);
    if (!panel) return [];
    return panel.isBatPanel
      ? [{ value: String(DrugTestingScheduleResult.BatPass), label: t('myDrugTesting.uploadResults.resultOptions.batPass') }]
      : [{ value: String(DrugTestingScheduleResult.Negative), label: t('myDrugTesting.uploadResults.resultOptions.negative') }];
  }, [panels, selectedPanelId, t]);

  return {
    projects,
    panels,
    types,
    results,
    isLoadingProjects,
    isLoadingPanels,
    isLoadingTypes,
  };
};
